import { useEffect, useState } from "react";
import { MonitorPlay } from "lucide-react";
import { Link } from "react-router-dom";
import { guestOpacApi, type GuestOpacResourceSummary } from "@/lib/api/guest-opac";
import { GuestCatalogSectionHeader } from "@/components/guest-catalog/GuestCatalogSectionHeader";
import { GuestCatalogSectionCard } from "@/components/guest-catalog/GuestCatalogSectionCard";
import { GuestCatalogItemView } from "@/components/guest-catalog/GuestCatalogItemView";
import { GuestCatalogCardsSkeleton } from "@/components/guest-catalog/GuestCatalogSkeleton";
import type { GuestCatalogViewMode } from "@/components/guest-catalog/guestCatalogViewMode";
import { Button } from "@/components/ui/button";

const DIGITAL_PREVIEW_SIZE = 8;

interface GuestCatalogDigitalSectionProps {
  tenantSlug: string;
  viewMode: GuestCatalogViewMode;
  loading?: boolean;
}

export function GuestCatalogDigitalSection({
  tenantSlug,
  viewMode,
  loading: externalLoading,
}: GuestCatalogDigitalSectionProps) {
  const [items, setItems] = useState<GuestOpacResourceSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    guestOpacApi
      .listCatalog({ type: "digital", page: 1, pageSize: DIGITAL_PREVIEW_SIZE }, tenantSlug)
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  const showSkeleton = loading || externalLoading;

  if (!showSkeleton && items.length === 0) return null;

  return (
    <GuestCatalogSectionCard>
      <section className="space-y-4">
        <GuestCatalogSectionHeader
          title="Digital"
          icon={MonitorPlay}
          description={showSkeleton ? undefined : "E-books, documents, and other digital resources"}
          action={
            !showSkeleton && items.length > 0 ? (
              <Button variant="link" className="h-auto p-0 text-primary" asChild>
                <Link to="/opac?type=digital">View all</Link>
              </Button>
            ) : undefined
          }
        />
        {showSkeleton ? (
          <GuestCatalogCardsSkeleton count={4} />
        ) : (
          <GuestCatalogItemView items={items} viewMode={viewMode} tenantSlug={tenantSlug} />
        )}
      </section>
    </GuestCatalogSectionCard>
  );
}
